/*
 * /emergency-plan (page)
 */
import { createFileRoute } from "@tanstack/react-router"
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Divider,
} from "@psychology/react-ui"
import { Container, Page, PageHeader } from "@psychology/react-ui"
import { ArrowLeft, ExternalLink, Phone, ShieldAlert } from "lucide-react"
import { trpc } from "../../trpc"
import { authPageLoader } from "../../utils/auth.utils"
// import { EmergencyContactDialog } from "../../../features/emergency/components/emergency-contact-dialog"
// import { EmergencyContactsList } from "../../../features/emergency/components/emergency-contacts-list"

export const Route = createFileRoute("/emergency/plan")({
	component: EmergencyPlanPage,
	loader: authPageLoader,
})

function EmergencyPlanPage() {
	const { user } = Route.useLoaderData()

	const { data, isLoading } = trpc.emergencyContact.getAllByFilter.useQuery(
		{ where: { userId: user?.id }, pagination: { limit: 10 } },
		{ enabled: !!user },
	)

	if (isLoading) {
		return <div>Loading entries...</div>
	}

	console.log("Data", { data, user })

	// const contacts: any[] = [] // contactsResult.success ? contactsResult.data || [] : []
	const contactCount = 0 // countResult.success ? countResult.data || 0 : 0
	const canAddMore = contactCount < 3

	return (
		<Page className={"p-2 sm:p-4 md:p-6 lg:p-8"} centerVertical={false}>
			<Container width={"xl"} padding={"lg"} gap={"sm"}>
				<PageHeader
					title={"Emergency Plan"}
					left={{
						href: "/dashboard",
						label: "Dashboard",
						Icon: <ArrowLeft size={20} />,
					}}
				/>
				<Divider />
				{/* Safety Disclaimer */}
				<Card className="border-orange-200 bg-orange-50">
					<CardHeader>
						<CardTitle className="flex items-center gap-2 text-orange-800">
							<ShieldAlert className="h-5 w-5" />
							Important Safety Information
						</CardTitle>
					</CardHeader>
					<CardContent className="text-orange-800">
						<div className="space-y-2">
							<p>
								<strong>
									This tool is for support planning only and is not a substitute
									for professional mental health care.
								</strong>
							</p>
							<p>
								<strong>In case of immediate crisis or emergency:</strong>
							</p>
							<ul className="list-disc list-inside space-y-1 ml-4">
								<li>Call 911 (US) or your local emergency number</li>
								<li>Go to your nearest emergency room</li>
								<li>
									Call the 988 Suicide & Crisis Lifeline: <strong>988</strong>
								</li>
								<li>
									Text &quot;HELLO&quot; to 741741 for the Crisis Text Line
								</li>
							</ul>
						</div>
					</CardContent>
				</Card>

				{/* Emergency Contacts Section */}
				<Card>
					<CardHeader>
						<div className="flex items-center justify-between">
							<div>
								<CardTitle className="flex items-center gap-2">
									<Phone className="h-5 w-5" />
									Emergency Contacts ({contactCount}/3)
								</CardTitle>
								<CardDescription>
									People you can reach out to during difficult times
								</CardDescription>
							</div>
							{/* {canAddMore && (
								<EmergencyContactDialog>
									<Button>
										<Plus className="h-4 w-4 mr-2" />
										Add Contact
									</Button>
								</EmergencyContactDialog>
							)} */}
						</div>
					</CardHeader>
					<CardContent>
						{/* <EmergencyContactsList contacts={contacts} /> */}
						{!canAddMore && (
							<div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
								<p className="text-sm text-blue-800">
									You&apos;ve reached the maximum of 3 emergency contacts.
									Remove an existing contact to add a new one.
								</p>
							</div>
						)}
					</CardContent>
				</Card>

				{/* Crisis Resources */}
				<Card>
					<CardHeader>
						<CardTitle className="flex items-center gap-2">
							<ExternalLink className="h-5 w-5" />
							Professional Crisis Resources
						</CardTitle>
						<CardDescription>
							24/7 professional crisis support services
						</CardDescription>
					</CardHeader>
					<CardContent>
						<div className="grid gap-4 md:grid-cols-2">
							<div className="p-4 border rounded-lg">
								<h3 className="font-semibold mb-2">
									988 Suicide & Crisis Lifeline
								</h3>
								<p className="text-sm text-muted-foreground mb-2">
									Free, confidential crisis support 24/7
								</p>
								<div className="space-y-1">
									<p className="font-mono text-lg">Call: 988</p>
									<p className="text-sm">
										<a
											href="https://988lifeline.org"
											target="_blank"
											rel="noopener noreferrer"
											className="text-blue-600 hover:underline"
										>
											988lifeline.org →
										</a>
									</p>
								</div>
							</div>

							<div className="p-4 border rounded-lg">
								<h3 className="font-semibold mb-2">Crisis Text Line</h3>
								<p className="text-sm text-muted-foreground mb-2">
									Text-based crisis support, available 24/7
								</p>
								<div className="space-y-1">
									<p className="font-mono text-lg">Text: HELLO to 741741</p>
									<p className="text-sm">
										<a
											href="https://crisistextline.org"
											target="_blank"
											rel="noopener noreferrer"
											className="text-blue-600 hover:underline"
										>
											crisistextline.org →
										</a>
									</p>
								</div>
							</div>

							<div className="p-4 border rounded-lg">
								<h3 className="font-semibold mb-2">
									National Domestic Violence Hotline
								</h3>
								<p className="text-sm text-muted-foreground mb-2">
									Support for domestic violence situations
								</p>
								<div className="space-y-1">
									<p className="font-mono text-lg">Call: 1-800-799-7233</p>
									<p className="text-sm">
										<a
											href="https://thehotline.org"
											target="_blank"
											rel="noopener noreferrer"
											className="text-blue-600 hover:underline"
										>
											thehotline.org →
										</a>
									</p>
								</div>
							</div>

							<div className="p-4 border rounded-lg">
								<h3 className="font-semibold mb-2">LGBT National Hotline</h3>
								<p className="text-sm text-muted-foreground mb-2">
									Support for LGBTQ+ individuals
								</p>
								<div className="space-y-1">
									<p className="font-mono text-lg">Call: 1-888-843-4564</p>
									<p className="text-sm">
										<a
											href="https://lgbthotline.org"
											target="_blank"
											rel="noopener noreferrer"
											className="text-blue-600 hover:underline"
										>
											lgbthotline.org →
										</a>
									</p>
								</div>
							</div>
						</div>
					</CardContent>
				</Card>
			</Container>
		</Page>
	)
}
